import { useLazyQuery, useMutation } from "@apollo/client";
import { createContext, useEffect, useMemo, useState } from "react";
import { ADD_COMMENT, ADD_FIGURE, UPDATE_FIGURE, DELETE_COMMENT, DELETE_FIGURE } from "../../graphql/mutations/ResourceVersionCommentContext";
import getCommentsAndFigures from "../../graphql/queries/getCommentsAndFigures";
import { ANNOTATION_TYPE as AnnotationType } from "../../pages/OneStream/utils/types";  // TODO: Move types to a better location.

export const commentsContext = createContext();

const { Provider } = commentsContext;

const _initialState = {};

const CommentsProvider = ({ children }) => {
  const [comments, setComments] = useState(_initialState);
  const [annotations, setAnnotations] = useState([]);
  const [selectedFigureId, setSelectedFigureId] = useState(null);

  const [availableVersions, setAvailableVersions] = useState([]);
  const [currentVersion, setCurrentVersion] = useState();

  const [addComment, { data, loading, error }] = useMutation(ADD_COMMENT);
  const [addFigure, { data: figureData, loading: figureLoading, error: figureError }] = useMutation(ADD_FIGURE);
  const [updateFigure, { data: updateData, loading: updateLoading, error: updateError }] = useMutation(UPDATE_FIGURE);

  const [deleteComment] = useMutation(DELETE_COMMENT);
  const [deleteFigure] = useMutation(DELETE_FIGURE);

  const [fetchComments, { data: commentData, loading: commentLoading, error: commentError }] = useLazyQuery(getCommentsAndFigures)

  const add = ({ comment, figureIds, pageNo }) => {
    addComment({
      variables: {
        versionId: currentVersion.versionId,
        pageNo: pageNo,
        comment: comment,
        figureIds,
      }
    });

    setSelectedFigureId(null);  // Deselect any selected annotation.
  };

  // Fetch initial comments for a product
  const loadComments = (productId) => {
    if (!productId) return;
    fetchComments({
      variables: { productId },
    });
  };

  useEffect(() => {
    if (commentData) {
      const productComments = commentData.productComments || [];

      // Flatten all comments across product versions and group by pageNo
      const commentsByPage = productComments.reduce((acc, productComment) => {
        const { comments = [], } = productComment;

        comments.forEach(({ pageNo, ...commentDetails }) => {
          if (!acc[pageNo]) acc[pageNo] = [];
          acc[pageNo].push({ pageNo, ...commentDetails });
        });

        return acc;
      }, {});

      const annotationsData = productComments.flatMap((productComment) =>
        productComment.figures?.map(({ id, resourceVersionId, figureData }) => ({
          id,
          resourceVersionId,
          ...JSON.parse(figureData),
        })) || []
      );

      setComments((prevComments) => ({
        ...prevComments,
        ...commentsByPage,
      }));

      setAnnotations(annotationsData);

      const versions = productComments
        .map(version => {
          const { versionId, versionNumber, pageCount, isPdf } = version;
          return { versionId, versionNumber, pageCount, isPdf };
        });

      const latestVersion = versions[0];  // Versions are ordered from last to first.

      setAvailableVersions(versions);
      setCurrentVersion(latestVersion);  // Select the latest version.
    }
  }, [commentData]);

  const addAnnotation = (pageNo, theAnnotation) => {
    const annotation = normalizeAnnotation(theAnnotation);

    addFigure({
      variables: {
        versionId: currentVersion.versionId,
        pageNo: pageNo,
        figureData: JSON.stringify(annotation),
      },
      onCompleted: (response) => {
        const newAnnotationId = response.resourceComments.addFigure.id; // Assuming the mutation response includes the new ID
        const savedAnnotation = { id: newAnnotationId, resourceVersionId: currentVersion.versionId, ...annotation };

        // Update the annotations state with the new annotation, including its ID
        setAnnotations((prevAnnotations) => [...prevAnnotations, savedAnnotation]);

        // Select the new annotation.
        setSelectedFigureId(newAnnotationId);
      },
      onError: (err) => {
        console.error("Failed to add annotation:", err);
      },
    });
  };

  /**
   * Normalizes the position and dimensions of an annotation.
   * 
   * Note:  Use only while creating an annotation.
   * 
   * @param {any} annotation - The annotation (shape) to be normalized.
   */
  const normalizeAnnotation = (annotation) => {
    let { subType, x, y, width, height } = annotation;

    const negativeWidth = (width < 0);
    const negativeHeight = (height < 0);

    switch (subType) {
      case AnnotationType.CLOUD:
      case AnnotationType.RECTANGLE:
        if (negativeWidth) {
          x += width;
          width = -width;
        }
        if (negativeHeight) {
          y += height;
          height = -height;
        }
        break;
      case AnnotationType.ARROW:
      case AnnotationType.ELLIPSE:
      default:
        break;
    };

    return (negativeWidth || negativeHeight)
      ? { ...annotation, x, y, width, height }
      : annotation;
  };

  const updateAnnotation = (id, update) => {
    const annotation = annotations.filter((ann) => ann.id === id)[0];
    const { resourceVersionId } = annotation;
    const updatedData = { ...annotation, ...update };
    const commentIds = (comments[1 /* TODO */] || []).filter(n => n.connectedFigureIds.includes(id)).map(n => n.resourceVersionCommentId);

    delete updatedData.id;
    delete updatedData.resourceVersionId;

    updateFigure({
      variables: {
        figureId: id,
        versionId: resourceVersionId,
        figureData: JSON.stringify(updatedData), // The updated figure data without id and resourceVersionId.
        commentIds,
      },
    });

    // Update the local state with id and resourceVersionId reattached.
    const updatedAnnotations = annotations.map((ann) =>
      ann.id === id ? { id, resourceVersionId, ...updatedData } : ann
    );

    setAnnotations(updatedAnnotations);
  };

  const removeComment = async (commentId) => {

    try {
      const { data } = await deleteComment({ variables: { commentId } });

      if (data?.resourceComments?.deleteComment) {
        setComments((prevComments) => {
          const updatedComments = { ...prevComments };
          for (const pageNo in updatedComments) {
            updatedComments[pageNo] = updatedComments[pageNo].filter(c => c.resourceVersionCommentId !== commentId);
          }
          return updatedComments;
        });

        setAnnotations((prevAnnotations) =>
          prevAnnotations.map((ann) => ({
            ...ann,
            connectedCommentIds: ann.connectedCommentIds?.filter(id => id !== commentId),
          }))
        );
      }
    } catch (error) {
      console.error("Failed to delete comment:", error);
    }
  };

  const removeAnnotation = async (annotationId) => {
    try {
      const { data } = await deleteFigure({ variables: { figureId: annotationId } });

      if (data?.resourceComments?.deleteFigure) {
        setAnnotations((prevAnnotations) =>
          prevAnnotations.filter((ann) => ann.id !== annotationId)
        );
      }
    } catch (error) {
      console.error("Failed to delete annotation:", error);
    }
  };

  useEffect(() => {
    if (data) {
      const { pageNo } = data.resourceComments.addComment;
      setComments(prevComments => ({
        ...prevComments,
        // Do not use pageComments here (no filtering should be made).
        [pageNo]: [...(comments[pageNo] || []), data.resourceComments.addComment],
      }));
    }
  }, [data]);

  const changeVersion = (versionNumber) => {
    const newVersion = availableVersions.find(n => n.versionNumber === versionNumber);
    if (!newVersion) {
      console.log("** invalid version", { versionNumber });
      return;
    }
    setSelectedFigureId(null);
    setCurrentVersion(newVersion);
  };

  const pageComments = (pageNo) => ((comments[pageNo] || []).filter(n => n.resourceVersionId === currentVersion.versionId));

  const filteredAnnotations = useMemo(
    () => (annotations || []).filter(n => n.resourceVersionId === currentVersion.versionId),
    [annotations, currentVersion]
  );

  return (
    <Provider value={{
      add,
      clear: () => setComments(_initialState),
      removeComment,
      removeAnnotation,
      comments,
      pageCommentCount: (pageNo) => pageComments(pageNo).length,
      pageComments,
      loadComments,
      commentLoading,
      commentError,
      annotations: filteredAnnotations,
      addAnnotation,
      normalizeAnnotation,
      updateAnnotation,
      selectedFigureId,
      setSelectedFigureId,
      availableVersions,
      currentVersionNumber: currentVersion?.versionNumber,
      changeVersion,
    }}>
      {children}
    </Provider>
  );
};

export default CommentsProvider;
